import { ListItemText, Tooltip, Typography } from "@mui/material";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { useCallback, useId, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import {
	CoreAvatar,
	CoreButton,
	CoreCheckbox,
	CoreChip,
	CoreDataView,
	CoreDataViewColumn,
	CoreMenuButton,
	CoreMenuItem,
	CorePagination,
	CoreSearchField,
	CoreSkeletonWrapper,
	coreToast,
	OverflowText,
	SortConfig,
} from "../../core";
import { useMobileLayout } from "../../hooks";
import i18nPackage from "../../i18n/i18n";
import { ExemptedStudent } from "../../types/common";
import { ConditionalWrapper, TooltipWrapper } from "../../wrapper";
import EditQuizFilter from "../components/EditQuizFilter";
import { useEditQuizToast } from "../context/EditQuizContext";
import { ViewDetailsModal } from "../edit-quiz-modals";
import SelectAccommodationModal from "../edit-quiz-modals/selectAccommodationModal";
import StepDescription from "../edit-quiz-steps/CommonStepComponents";
import AccommodationIcon from "../images/confirmation/accommodation-step.svg?react";
import {
	BaseListContainer,
	BaseListFooterContainer,
	BaseListHeaderContainer,
	BaseListInfoArea,
	EditQuizNoResults,
} from "../proctoring-options/option-lists/OptionListsComponents";
import { getProfileName } from "../profile";
import { ProfileListProps } from "../profile/ProfileList";
import {
	ACCOMMODATION_FILTER,
	AccommodationFilterType,
	ExemptionInfo,
	getProfileColor,
	removeStudentsFromExemptRoster,
	updateStudentsInRoster,
	useAccommodationFilter,
	useExemptionModals,
	usePaginatedRows,
	useStudentSelection,
} from "./exemptionHelper";
import styles from "./ExemptionSection.module.css";

type AccommodationStatusCellProps = {
	isExempted: boolean;
	proctoringOptionProfileId: ExemptedStudent["proctoringOptionProfileId"];
	profilesMap: ExemptionSectionProps["profilesMap"];
};

function AccommodationStatusCell({ isExempted, proctoringOptionProfileId, profilesMap }: AccommodationStatusCellProps) {
	const profileName =
		isExempted && proctoringOptionProfileId
			? getProfileName(profilesMap.get(proctoringOptionProfileId))
			: getProfileName(undefined);

	const profileColor = getProfileColor(profileName);

	if (!isExempted)
		return (
			<Tooltip title={i18nPackage.t("editQuizPage.accommodationStep.noAccommodationTooltip")}>
				<Typography>{i18nPackage.t("editQuizPage.accommodationStep.noAccommodation")}</Typography>
			</Tooltip>
		);

	return isExempted && proctoringOptionProfileId ? (
		<CoreChip
			variant="rectangular"
			tooltip={i18nPackage.t("editQuizPage.accommodationStep.modifiedProctoringTooltip", { profileName })}
			chipColor={profileColor}
			className={styles.profileBadgeTag}
			tooltipProps={{
				placement: "top",
			}}
		>
			{profileName}
		</CoreChip>
	) : (
		<CoreChip
			chipColor="#F9E4F5"
			variant="rectangular"
			className={styles.notProctoredBadge}
			tooltip={i18nPackage.t("editQuizPage.accommodationStep.notProctoredTooltip")}
			tooltipProps={{
				placement: "top",
			}}
		>
			{i18nPackage.t("editQuizPage.accommodationStep.notProctored")}
		</CoreChip>
	);
}

type NameCellProps = {
	student: ExemptedStudent;
	isMobileLayout: boolean;
};

function NameCell({ student, isMobileLayout }: NameCellProps) {
	const { name, userId } = student;
	return (
		<div className={styles.nameCell}>
			<CoreAvatar colorConfig={userId}>{name[0]}</CoreAvatar>
			<ConditionalWrapper
				condition={isMobileLayout}
				whenTrue={(children) => <Typography>{children}</Typography>}
				whenFalse={(children) => <OverflowText>{children}</OverflowText>}
			>
				{name}
			</ConditionalWrapper>
		</div>
	);
}

type AccommodationToolbarProps = {
	onSearch: (value: string) => void;
	ariaControls: string;
	totalResults: number;
	searchFilter: string;
	activeStatus: AccommodationFilterType;
	onFilterChange: (status: AccommodationFilterType) => void;
};

function AccommodationToolbar({
	activeStatus,
	ariaControls,
	onFilterChange,
	onSearch,
	totalResults,
	searchFilter,
}: AccommodationToolbarProps) {
	return (
		<div className={styles.searchToolbarWrapper}>
			<CoreSearchField
				autoComplete="off"
				noForm
				label={i18nPackage.t("editQuizPage.accommodationStep.searchPlaceholder")}
				onSearch={onSearch}
				ariaControls={ariaControls}
				totalResults={totalResults}
				value={searchFilter}
			/>
			<EditQuizFilter<AccommodationFilterType>
				value={activeStatus}
				onChange={onFilterChange}
				ariaLabel={i18nPackage.t("editQuizPage.accommodationStep.filter.ariaLabel")}
				renderValue={(value) => {
					return (
						<Typography component={"span"}>
							{i18nPackage.t(`editQuizPage.accommodationStep.filter.${value}`)}
						</Typography>
					);
				}}
			>
				{ACCOMMODATION_FILTER.map((val) => {
					return (
						<CoreMenuItem value={val} key={val}>
							<ListItemText primary={i18nPackage.t(`editQuizPage.accommodationStep.filter.${val}`)} />
						</CoreMenuItem>
					);
				})}
			</EditQuizFilter>
		</div>
	);
}

type StudentActionMenuProps = {
	student: ExemptedStudent;
	actions: Array<{
		name: (student: ExemptedStudent) => string;
		hide?: boolean | ((student: ExemptedStudent) => boolean);
		action: (student: ExemptedStudent) => void;
		ariaHasPopup?: boolean;
	}>;
};

function StudentActionMenu({ student, actions }: StudentActionMenuProps) {
	const [menuShowing, setMenuShowing] = useState(false);

	// Don't show menu button if every menu action is hidden
	if (actions.every((a) => typeof a.hide === "function" && a.hide(student))) return null;

	return (
		<CoreMenuButton
			menuShowing={menuShowing}
			setMenuShowing={setMenuShowing}
			aria-label={i18nPackage.t("editQuizPage.accommodationStep.actionsAriaLabel")}
			className={styles.actionMenu}
		>
			{actions.map((action) =>
				(typeof action.hide === "function" ? action.hide(student) : action.hide) ? null : (
					<CoreMenuItem
						key={action.name(student)}
						onClick={() => {
							action.action(student);
							setMenuShowing(false);
						}}
						aria-haspopup={action.ariaHasPopup}
					>
						<ListItemText primary={action.name(student)} />
					</CoreMenuItem>
				),
			)}
		</CoreMenuButton>
	);
}

type TableToolbarProps = {
	numSelected: number;
	onUpdateAccommodation?: () => void;
	onRemoveAccommodation?: () => void;
	isAddAccommodation?: boolean;
};

function TableToolbar({
	numSelected,
	onRemoveAccommodation,
	onUpdateAccommodation,
	isAddAccommodation,
}: TableToolbarProps) {
	return (
		<div className={styles.tableToolbar}>
			<Typography className={styles.selected}>
				{i18nPackage.t("editQuizPage.accommodationStep.selectedTestTakers", { count: numSelected })}
			</Typography>
			<div className={styles.buttonWrapper}>
				{onUpdateAccommodation && (
					<CoreButton onClick={onUpdateAccommodation} aria-haspopup="dialog">
						{isAddAccommodation
							? i18nPackage.t("editQuizPage.accommodationStep.addAccommodation")
							: i18nPackage.t("editQuizPage.accommodationStep.updateAccommodation")}
					</CoreButton>
				)}
				{onRemoveAccommodation && (
					<CoreButton onClick={onRemoveAccommodation} color="cancel">
						{i18nPackage.t("editQuizPage.accommodationStep.removeAccommodation")}
					</CoreButton>
				)}
			</div>
		</div>
	);
}

export type ExemptionSectionProps = {
	loading?: boolean;
	profilesMap: Map<number, ProctoringOptionProfileV2>;
	exemptionInfo: ExemptionInfo;
	editInstitutionExemptions?: boolean;
	onRosterChange: (newRoster: ExemptionInfo) => void;
} & Pick<
	ProfileListProps,
	| "verityInstituteSettings"
	| "enforcedProfilesOnly"
	| "optionRelatedInstituteSettings"
	| "proctoringData"
	| "canDeleteProfile"
	| "onDelete"
>;

export default function ExemptionSection({
	loading,
	profilesMap,
	exemptionInfo,
	editInstitutionExemptions,
	enforcedProfilesOnly,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	proctoringData,
	canDeleteProfile,
	onDelete,
	onRosterChange,
}: ExemptionSectionProps) {
	const [curPage, setCurPage] = useState<number>(1);
	const [recentIds, setRecentIds] = useState<Set<number>>(new Set());
	const controlId = useId();
	const isMobileLayout = useMobileLayout();
	const toastContainerId = useEditQuizToast();

	const {
		filteredRoster,
		setSortConfig,
		sortConfig,
		clearFilters,
		activeStatus,
		searchFilter,
		setActiveStatus,
		setSearchFilter,
	} = useAccommodationFilter({
		exemptionInfo,
		recentIds,
		profilesMap,
	});

	const { paginatedRows, totalPages } = usePaginatedRows(filteredRoster, curPage, setCurPage);

	const {
		allSelected,
		clearSelection,
		handleSelectAll,
		selectedIds,
		selectedStudents,
		isIndeterminate,
		toggleSelectOne,
		isAllSelectedNoAccommodation,
		isStudentExempted,
		canRemoveAccommodations,
	} = useStudentSelection(filteredRoster, exemptionInfo);

	const { viewModal, curStudentProfile, currentStudent, accommodationModal } = useExemptionModals(
		profilesMap,
		selectedStudents,
	);

	const saveHandler = (selectedProfileId: number | undefined, selectedNotes: string | undefined) => {
		if (!selectedStudents) return;

		const updatedIds = selectedStudents.map((s) => s.userId);
		setRecentIds(new Set(updatedIds));

		const isAdding = selectedStudents.some((s) => !isStudentExempted(s.userId));

		const newRoster = updateStudentsInRoster(exemptionInfo, selectedStudents, selectedProfileId, selectedNotes);

		onRosterChange(newRoster);

		coreToast.dismiss();
		if (!editInstitutionExemptions) {
			const toastOptions = toastContainerId ? { containerId: toastContainerId } : undefined;
			if (selectedStudents?.length === 1) {
				const studentName = selectedStudents[0].name || i18nPackage.t("editQuizPage.accommodationStep.defaultName");
				coreToast.success(
					i18nPackage.t("editQuizPage.accommodationStep.accommodationsSaved_one", {
						name: studentName,
					}),
					toastOptions,
				);
			} else {
				coreToast.success(i18nPackage.t("editQuizPage.accommodationStep.accommodationsSaved_other"), toastOptions);
			}
		}
		setCurPage(1);
		clearSelection();

		// If user added accommodation to students, clear filters so user can see the new changes at the top
		if (isAdding) {
			clearFilters();
		}
	};

	const handleRemoveAccommodations = useCallback(() => {
		const ids = Array.from(selectedIds).map(Number);
		const updatedRoster = removeStudentsFromExemptRoster(exemptionInfo, ids);

		onRosterChange(updatedRoster);
		clearSelection();
	}, [clearSelection, exemptionInfo, onRosterChange, selectedIds]);

	const columns = useMemo(
		(): CoreDataViewColumn<ExemptedStudent>[] => [
			{
				key: "userId",
				header: (
					<CoreCheckbox
						checked={allSelected}
						onChange={(e) => handleSelectAll(e.target.checked)}
						inputProps={{
							"aria-label": allSelected
								? i18nPackage.t("editQuizPage.accommodationStep.unselectAll")
								: i18nPackage.t("editQuizPage.accommodationStep.selectAll"),
						}}
						disabled={paginatedRows.filter((s) => s.type !== "institution").length === 0}
						indeterminate={isIndeterminate}
					/>
				),
				cell: (student) => {
					const checked = selectedIds.has(String(student.userId));
					const ariaKey = checked ? "unselectStudent" : "selectStudent";
					return (
						<TooltipWrapper
							condition={student.type === "institution" && !editInstitutionExemptions}
							title={i18nPackage.t("editQuizPage.accommodationStep.institutionToolTip")}
						>
							<CoreCheckbox
								disabled={student.type === "institution" && !editInstitutionExemptions}
								onChange={(e) => toggleSelectOne(String(student.userId), e.target.checked)}
								checked={checked}
								aria-label={i18nPackage.t(`editQuizPage.accommodationStep.${ariaKey}`, { student: student.name })}
							/>
						</TooltipWrapper>
					);
				},
				width: "50px",
			},
			{
				key: "name",
				header: i18nPackage.t("editQuizPage.accommodationStep.testTaker"),
				cell: (student) => <NameCell student={student} isMobileLayout={isMobileLayout} />,
				sortable: true,
				allowOverflow: isMobileLayout,
			},
			{
				key: "proctoringOptionProfileId",
				header: i18nPackage.t("editQuizPage.accommodationStep.accommodationStatus"),
				cell: (student) => (
					<AccommodationStatusCell
						isExempted={isStudentExempted(student.userId)}
						proctoringOptionProfileId={student.proctoringOptionProfileId}
						profilesMap={profilesMap}
					/>
				),
				sortable: true,
				allowOverflow: isMobileLayout,
			},
			{
				key: "selection" as never,
				width: "50px",
				header: i18nPackage.t("editQuizPage.accommodationStep.actionsHeader"),
				cell: (student) => (
					<StudentActionMenu
						student={student}
						actions={[
							{
								name: () => i18nPackage.t("editQuizPage.accommodationStep.menu.view"),
								action: viewModal.open,
								hide: (student) => !isStudentExempted(student.userId),
								ariaHasPopup: true,
							},
							{
								name: (student) =>
									isStudentExempted(student.userId)
										? i18nPackage.t("editQuizPage.accommodationStep.menu.edit")
										: i18nPackage.t("editQuizPage.accommodationStep.menu.add"),
								action: (student) => {
									toggleSelectOne(String(student.userId), true);
									accommodationModal.open();
								},
								hide: (student) => {
									return !editInstitutionExemptions && student.type === "institution";
								},
								ariaHasPopup: true,
							},
							{
								name: () => i18nPackage.t("editQuizPage.accommodationStep.menu.remove"),
								action: (student) => {
									const updatedRoster = removeStudentsFromExemptRoster(exemptionInfo, [student.userId]);

									onRosterChange(updatedRoster);

									toggleSelectOne(String(student.userId), false);
								},
								hide: (student) => {
									return (
										(!editInstitutionExemptions && student.type === "institution") || !isStudentExempted(student.userId)
									);
								},
							},
						]}
					/>
				),
			},
		],
		[
			accommodationModal,
			allSelected,
			editInstitutionExemptions,
			exemptionInfo,
			handleSelectAll,
			isIndeterminate,
			isMobileLayout,
			isStudentExempted,
			onRosterChange,
			paginatedRows,
			profilesMap,
			selectedIds,
			toggleSelectOne,
			viewModal.open,
		],
	);

	const clearRecentIds = useCallback(() => {
		if (recentIds.size > 0) {
			setRecentIds(new Set());
		}
	}, [recentIds.size]);

	const handleSortConfig = (newSort: SortConfig<ExemptedStudent>[]) => {
		setSortConfig(newSort);
		clearRecentIds();
	};

	const handleSearch = useCallback(
		(value: string) => {
			setSearchFilter(value.toLowerCase());
			setCurPage(1);
			clearRecentIds();
		},
		[clearRecentIds, setSearchFilter],
	);

	const handleFilterChange = useCallback(
		(status: AccommodationFilterType) => {
			setActiveStatus(status);
			setCurPage(1);
			clearRecentIds();
		},
		[clearRecentIds, setActiveStatus],
	);

	return (
		<>
			<div className={styles.accommodationsPage}>
				<StepDescription loading={loading}>
					{i18nPackage.t("editQuizPage.accommodationStep.accommodationsHeader")}
				</StepDescription>
				<StepDescription loading={loading}>
					<Trans
						i18n={i18nPackage}
						i18nKey={"editQuizPage.accommodationStep.accommodationsDisclaimer"}
						components={{ b: <strong /> }}
					/>
				</StepDescription>
				<BaseListContainer>
					<CoreSkeletonWrapper loading={loading}>
						<BaseListHeaderContainer>
							<BaseListInfoArea
								title={i18nPackage.t("editQuizPage.accommodationStep.availableTestTakers")}
								titleVariant="h2"
								icon={<AccommodationIcon />}
							/>
							<AccommodationToolbar
								activeStatus={activeStatus}
								ariaControls={controlId}
								onFilterChange={handleFilterChange}
								onSearch={handleSearch}
								searchFilter={searchFilter}
								totalResults={filteredRoster.length}
							/>
							{selectedIds.size > 0 && (
								<TableToolbar
									numSelected={selectedIds.size}
									isAddAccommodation={isAllSelectedNoAccommodation}
									onUpdateAccommodation={accommodationModal.open}
									onRemoveAccommodation={canRemoveAccommodations ? handleRemoveAccommodations : undefined}
								/>
							)}
						</BaseListHeaderContainer>
					</CoreSkeletonWrapper>
					<BaseListFooterContainer>
						<CoreDataView
							data={paginatedRows}
							columns={columns}
							loading={loading}
							id={controlId}
							disableInternalSorting
							sortConfig={sortConfig}
							onSortChange={handleSortConfig}
							renderers={{
								noResults: <EditQuizNoResults />,
							}}
							slots={{
								tableBodyCell: {
									className: styles.accommodationCell,
								},
								tableHeadCell: {
									className: styles.accommodationCell,
								},
								table: {
									className: styles.accommodationTable,
								},
								tableBodyRow: {
									className: styles.accommodationRow,
								},
							}}
						/>
						{totalPages > 1 && (
							<CorePagination
								count={totalPages}
								onChange={(_e, page) => setCurPage(page)}
								defaultPage={1}
								size={"medium"}
								page={curPage}
								paginationWrap={false}
							/>
						)}
					</BaseListFooterContainer>
				</BaseListContainer>
			</div>
			{accommodationModal.isOpen && (
				<SelectAccommodationModal
					profiles={Array.from(profilesMap.values())}
					activeProfileId={accommodationModal.profileId}
					studentAccommodationNotes={accommodationModal.notes}
					proctoringData={proctoringData}
					saveHandler={saveHandler}
					onClose={accommodationModal.close}
					enforcedProfilesOnly={enforcedProfilesOnly}
					optionRelatedInstituteSettings={optionRelatedInstituteSettings}
					verityInstituteSettings={verityInstituteSettings}
					canDeleteProfile={canDeleteProfile}
					onDelete={onDelete}
				/>
			)}
			{viewModal.isOpen && currentStudent && (
				<ViewDetailsModal
					exemptedStudent={currentStudent}
					profile={curStudentProfile}
					onClose={viewModal.close}
					{...(curStudentProfile
						? {
								configuration: {
									optionRelatedInstituteSettings,
									proctoringData,
									verityInstituteSettings,
									proctoringOption: curStudentProfile.proctoringOption,
								},
							}
						: {})}
					title={i18nPackage.t("editQuizPage.accommodationStep.accommodationDetails")}
					description={i18nPackage.t("editQuizPage.accommodationStep.viewDetailsDesc")}
				/>
			)}
		</>
	);
}
